// src/components/MemberProfilePage/ViolationsList.js
import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Card,
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Pagination,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../utils/api";

const ALLOWED_PALETTE_KEYS = new Set([
  "primary",
  "secondary",
  "error",
  "warning",
  "info",
  "success",
]);

const ITEMS_PER_PAGE = 10;

const normalizePaletteKey = (value, fallback = "secondary") => {
  const key = String(value || "").toLowerCase().trim();
  if (key === "inherit") return fallback;
  return ALLOWED_PALETTE_KEYS.has(key) ? key : fallback;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString();
};

const ViolationsList = ({ violations, violationTypes, navigate }) => {
  const [statusMap, setStatusMap] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoadingStatuses(true);
        const res = await api.get("/statuses/violations");

        const map = {};
        (res.data || []).forEach((s) => {
          const statusNameKey = String(s.statusname || "").toLowerCase().trim();
          map[statusNameKey] = {
            colorKey: normalizePaletteKey(s.color, "secondary"),
            icon: s.icon,
          };
        });

        setStatusMap(map);
      } catch (err) {
        console.error("Failed to load violation statuses:", err);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [violations]);

  const violationTypeMap = useMemo(() => {
    const map = {};
    (violationTypes || []).forEach((type) => {
      map[type.id] = type;
    });
    return map;
  }, [violationTypes]);

  const sortedViolations = useMemo(() => {
    return [...(violations || [])].sort((a, b) => {
      const dateA = a?.violated_at ? new Date(a.violated_at).getTime() : 0;
      const dateB = b?.violated_at ? new Date(b.violated_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [violations]);

  const totalPages = Math.ceil((sortedViolations.length || 0) / ITEMS_PER_PAGE);

  const paginatedViolations = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return sortedViolations.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedViolations, page]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (loadingStatuses) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!violations || violations.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No violations found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          This member does not currently have any recorded violations.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Grid container spacing={2.25}>
        {paginatedViolations.map((violation) => {
          const statusKey = String(violation.status || "").toLowerCase().trim();
          const paletteKey = statusMap[statusKey]?.colorKey || "secondary";
          const violationType =
            violationTypeMap[violation.ccr_violation_type_id]?.title ||
            "Type Not Found";

          return (
            <Grid item xs={12} key={violation.id}>
              <Card
                sx={{
                  ...pageCardSx,
                  p: { xs: 1.5, sm: 2, md: 2.25 },
                  borderLeft: "6px solid",
                  borderLeftColor: (theme) => theme.palette[paletteKey].main,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(47,99,173,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        <WarningAmberIcon sx={{ color: "#2f63ad" }} />
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#223142",
                            lineHeight: 1.2,
                          }}
                        >
                          VLT-{violation.id}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b778c",
                            mt: 0.25,
                          }}
                        >
                          {violationType}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 0.75, sm: 2.5 }}
                      sx={{ pl: { xs: 0, sm: "52px" } }}
                    >
                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          Violated:
                        </Box>{" "}
                        {formatDate(violation.violated_at)}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#6b778c" }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: "#223142" }}
                        >
                          Status:
                        </Box>{" "}
                        {violation.status || "Unknown"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.25,
                      flexDirection: { xs: "column", sm: "row" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <Chip
                      label={String(violation.status || "UNKNOWN").toUpperCase()}
                      color={paletteKey}
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        height: 32,
                        borderRadius: "999px",
                        "& .MuiChip-label": {
                          px: 1.25,
                        },
                      }}
                    />

                    <Button
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(`/home/violations/${violation.id}`)}
                      sx={secondaryButtonSx}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {sortedViolations.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(page * ITEMS_PER_PAGE, sortedViolations.length)} of{" "}
            {sortedViolations.length} violations
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ViolationsList;